import { variantProvider } from '@/app'
import { ActionIcon, ActivityIndicator, Text, View } from '@/components'
import { APIClient } from '@/services'
import { CommunityFeed } from '@/types'
import { useMediaQueryDown } from '@/utils'
import { motion } from 'framer-motion'
import React, { forwardRef, useCallback, useEffect } from 'react'
import { FeedCommentDrawer } from './CommentList'
import { FeedItem } from './FeedItem'
import { FeedDimensions } from './FeedItems/shared'
import { FilterButton } from './atoms/FilterButton'
import { SearchButton } from './atoms/SearchButton'
import { useFeedScroll } from './feedScroll'

type FeedListProps = ReturnType<typeof APIClient.Community.useCommunity>['listProps'] & {
  menuVisible?: boolean
}

const Parent = forwardRef<HTMLDivElement, React.PropsWithChildren>((props, ref) => {
  const isBelowXlargePlus = useMediaQueryDown('xlargePlus')
  const drawerVisible = FeedCommentDrawer.useIsVisible()

  return <motion.div
    ref={ref}
    {...props}
    css={styles.feed}
    transition={{ duration: 0.3 }}
    animate={{ width: (drawerVisible && !isBelowXlargePlus) ? '75vw' : '100vw' }}
  />
})

export const FeedList = (props: FeedListProps) => {
  const { data, isLoading, menuVisible } = props

  const {
    goToNext,
    goToPrevious,
    virtualizer,
    parentRef,
  } = useFeedScroll(props)

  const isEmpty = (data?.length <= 0 || !data) && !isLoading

  useEffect(() => {
    if (isEmpty) FeedCommentDrawer.close()
  }, [isEmpty])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement
      const isEditableTarget = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName) || target?.isContentEditable

      if (isEditableTarget || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        goToNext()
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        goToPrevious()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [goToNext, goToPrevious])

  const RenderItem = useCallback(({ item, index }: { item: CommunityFeed; index: number }) => {
    return (
      <FeedItem
        item={item}
        index={index}
        goToNext={goToNext}
      />
    )
  }, [])

  return <>
    <Parent ref={parentRef}>
      {isLoading ? null : (
        <div css={[styles.itemsWrapper, { height: `${virtualizer.getTotalSize()}px` }]}>
          {virtualizer.getVirtualItems()?.map((virtualItem) => {
            const item = data?.[virtualItem.index]

            if (!item) return null

            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualItem.start}px)`,
                  scrollSnapAlign: 'center',
                }}
              >
                <RenderItem
                  index={virtualItem.index}
                  item={item}
                />
              </div>
            )
          })}
        </div>
      )}

      {isEmpty || isLoading ? (
        <View variants={['column', 'gap:1', 'center', 'textCenter', 'fullWidth', 'fullHeight']}>
          {isLoading ? (
            <View variants={['bg:neutral1', 'padding:1', 'center', 'border-radius:rounded']}>
              <ActivityIndicator debugName='feed:loading' />
            </View>
          ) : <>
            <Text text='Not found!' variants={['h4', 'color:neutral1']} />
            <Text text='We cannot find any items at the moment.' variants={['p1', 'color:neutral1']} />
          </>}
        </View>
      ) : null}
    </Parent>

    <FilterButton />
    {!menuVisible ? <SearchButton /> : null}

    <FeedCommentDrawer.Container>
      <View style={styles.actions}>
        <ActionIcon
          variants={['feedAction', 'iconSize:4']}
          debugName=''
          name='arrow-up'
          onPress={goToPrevious}
        />
        <ActionIcon
          variants={['feedAction', 'iconSize:4']}
          debugName=''
          name='arrow-down'
          onPress={goToNext}
        />
      </View>

      <FeedCommentDrawer />
    </FeedCommentDrawer.Container>
  </>
}

const styles = variantProvider.createComponentStyle(theme => ({
  feed: {
    width: '100vw',
    height: 'calc(100dvh - (104px))',
    overflowX: 'hidden',
    scrollbarWidth: 'none',
    '-ms-overflow-style': 'none',
    scrollSnapType: 'y mandatory',
    overflowY: 'scroll',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',

    [theme.media.down('xlargePlus')]: {
      padding: 0,
    },
  },
  itemsWrapper: {
    position: 'relative',
    width: FeedDimensions.WIDTH,

    [theme.media.down(FeedDimensions.mobileBreakpoint)]: {
      width: '100%',
    },
  },
  actions: {
    ...theme.presets.column,
    ...theme.spacing.gap(1.2),
    ...theme.spacing.paddingRight(3),
    [theme.media.down('xlargePlus')]: {
      display: 'none',
    },
  },
  header: {
    ...theme.presets.absolute,
    top: theme.spacing.value(2),
    right: theme.spacing.value(3),
    left: theme.spacing.value(3),
    ...theme.presets.row,
    ...theme.presets.justifySpaceBetween,
    ...theme.presets.alignCenter,
    ...theme.spacing.gap(1),
  },
}), true)
